import { useEffect, useState } from 'react';
import { pedirFoto } from '../api/cliente';
import { colorDe, iniciales } from '../lib/personas';

interface Props {
  usuarioId: string;
  nombre: string;
  tamano?: number;
  className?: string;
}

/// Una sola descarga por persona durante la sesión: el mismo avatar aparece en
/// el encabezado, en «Quiénes van» y en la tabla de respuestas. Las URL blob:
/// no se revocan: son pocas y viven lo que dura la pestaña.
const fotos = new Map<string, Promise<string | null>>();

function urlDeFoto(usuarioId: string): Promise<string | null> {
  let pendiente = fotos.get(usuarioId);
  if (!pendiente) {
    pendiente = pedirFoto(usuarioId).then(
      (blob) => (blob ? URL.createObjectURL(blob) : null),
      () => {
        // Error pasajero (red, token): se reintenta la próxima vez que se monte.
        fotos.delete(usuarioId);
        return null;
      },
    );
    fotos.set(usuarioId, pendiente);
  }
  return pendiente;
}

/// Foto de perfil de Entra si existe; si no, las iniciales con un color
/// estable derivado del nombre. Mientras la foto carga también se ven las
/// iniciales. El 404 del backend es el caso normal para alguien que nunca
/// cargó foto, así que no se loguea como error.
export function Avatar({ usuarioId, nombre, tamano = 32, className = '' }: Props) {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let vigente = true;
    setUrl(null);
    void urlDeFoto(usuarioId).then((resultado) => {
      if (vigente) setUrl(resultado);
    });
    return () => {
      vigente = false;
    };
  }, [usuarioId]);

  const estilo = { width: tamano, height: tamano };
  const clases = `flex-none rounded-full object-cover ${className}`;

  if (!url) {
    return (
      <div
        className={`${clases} flex items-center justify-center font-titulo font-bold text-tinta`}
        style={{
          ...estilo,
          background: colorDe(nombre),
          fontSize: Math.max(9, Math.round(tamano * 0.37)),
        }}
        aria-hidden
      >
        {iniciales(nombre)}
      </div>
    );
  }

  return (
    <img
      src={url}
      alt=""
      width={tamano}
      height={tamano}
      decoding="async"
      className={`${clases} bg-white/5`}
      style={estilo}
      onError={() => setUrl(null)}
    />
  );
}
